/**
 * Authentication Service for Frontend
 *
 * Handles:
 * - Login flow with Azure AD redirect
 * - OAuth callback processing
 * - Logout with session cleanup
 * - Auth state checking on app load
 * - Current user info management
 */

import { apiClient } from './api-client';
import {
  setAuthLoading,
  setAuthenticated,
  setAuthError,
  logout as logoutStore,
  getAuthState,
  type User,
  type UserRole,
} from './auth-store';

/**
 * Response from /api/auth/login endpoint
 */
interface LoginResponse {
  authUrl: string;
}

/**
 * Response from /api/auth/logout endpoint
 */
interface LogoutResponse {
  logoutUrl: string;
}

/**
 * User data from backend (matches AuthenticatedUser type)
 */
interface BackendUser {
  id: string;
  email: string;
  displayName: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  roles: UserRole[];
  managerId?: string;
}

/**
 * Response from /api/auth/me endpoint
 */
interface AuthMeResponse {
  authenticated: boolean;
  user?: BackendUser;
}

/**
 * Convert backend user to frontend user format
 */
function mapBackendUserToUser(backendUser: BackendUser): User {
  const user: User = {
    id: backendUser.id,
    email: backendUser.email,
    displayName: backendUser.displayName,
    givenName: backendUser.givenName ?? '',
    surname: backendUser.surname ?? '',
    // Use the first role as the primary role, default to USER if no roles assigned
    role: backendUser.roles[0] ?? 'USER',
  };

  // Add optional properties only if they exist
  if (backendUser.jobTitle) {
    user.jobTitle = backendUser.jobTitle;
  }
  if (backendUser.managerId) {
    user.managerId = backendUser.managerId;
  }

  return user;
}

/**
 * Check if user is currently authenticated by calling /api/auth/me
 * Updates auth store with current user info
 *
 * @returns true if user is authenticated
 */
export async function checkAuthStatus(): Promise<boolean> {
  setAuthLoading(true);

  try {
    const response = await apiClient.get<AuthMeResponse>('/auth/me', {
      credentials: 'include', // Important for session cookie
    });

    const { authenticated, user } = response.data;

    if (authenticated && user) {
      const mappedUser = mapBackendUserToUser(user);
      // Use a short initial expiry (60 seconds) to trigger an early refresh
      // which will get the actual token expiry from the backend
      setAuthenticated(mappedUser, '', 60);
      return true;
    } else {
      logoutStore();
      return false;
    }
  } catch (error) {
    console.error('Error checking auth status:', error);
    logoutStore();
    return false;
  }
}

/**
 * Initiate login flow by redirecting to Azure AD
 * Gets the auth URL from backend and redirects the browser
 */
export async function login(): Promise<void> {
  setAuthLoading(true);

  try {
    const response = await apiClient.get<LoginResponse>('/auth/login', {
      credentials: 'include',
    });

    const { authUrl } = response.data;

    if (authUrl) {
      // Redirect to Azure AD login page
      window.location.href = authUrl;
    } else {
      throw new Error('No auth URL received from server');
    }
  } catch (error) {
    console.error('Error initiating login:', error);
    setAuthError(error instanceof Error ? error.message : 'Failed to initiate login');
    throw error;
  }
}

/**
 * Handle OAuth callback by checking auth status
 * Called after redirect back from Azure AD
 *
 * @returns true if callback was successful and user is authenticated
 */
export async function handleAuthCallback(): Promise<boolean> {
  // The backend callback endpoint handles token exchange and sets session
  // We just need to check if we're now authenticated
  return checkAuthStatus();
}

/**
 * Logout the current user
 * Clears local state and redirects to Azure AD logout
 *
 * @param redirectToAzure - Whether to redirect to Azure AD logout (default: true)
 */
export async function logout(redirectToAzure = true): Promise<void> {
  try {
    if (redirectToAzure) {
      const response = await apiClient.post<LogoutResponse>('/auth/logout', undefined, {
        credentials: 'include',
      });

      const { logoutUrl } = response.data;

      // Clear local state first
      logoutStore();

      if (logoutUrl) {
        // Redirect to Azure AD logout
        window.location.href = logoutUrl;
      }
    } else {
      // Just call logout endpoint without redirect
      await apiClient.post('/auth/logout', undefined, {
        credentials: 'include',
      });
      logoutStore();
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Still clear local state even if backend call fails
    logoutStore();
    throw error;
  }
}

/**
 * Get the current authenticated user
 *
 * @returns Current user or null if not authenticated
 */
export function getCurrentUser(): User | null {
  const state = getAuthState();
  return state.user;
}

/**
 * Check if user is authenticated (based on local state)
 *
 * @returns true if user appears to be authenticated
 */
export function isAuthenticated(): boolean {
  const state = getAuthState();
  return state.isAuthenticated;
}

/**
 * Check if auth is currently loading
 *
 * @returns true if auth check is in progress
 */
export function isAuthLoading(): boolean {
  const state = getAuthState();
  return state.isLoading;
}

/**
 * Check if user has a specific role
 *
 * @param role - Role to check for
 * @returns true if user has the role
 */
export function hasRole(role: UserRole): boolean {
  const user = getCurrentUser();
  return user?.role === role;
}

/**
 * Check if user is an admin
 *
 * @returns true if user has ADMIN role
 */
export function isAdmin(): boolean {
  return hasRole('ADMIN');
}

/**
 * Check if user is an assistant
 *
 * @returns true if user has ASSISTANT role
 */
export function isAssistant(): boolean {
  return hasRole('ASSISTANT');
}
